import {
  fmtDate,
  fmtVisitDay,
  fmtVisitTime,
  fromDateTimeInput,
  siteDayKey,
  siteTodayKey,
  toDateTimeInput,
  visitActualDateLabel,
  visitCalendarWhen,
  visitWhen,
} from '@/lib/dates';
import { useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { AlertTriangle, Calendar as CalendarIcon, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Info, LayoutList, Loader2, Plus, X } from 'lucide-react';
import { visits, studies, subjects, type VisitRow, type StudyRow, type SubjectRow } from '@/api/client';
import { PageHeader } from '@/components/layout/PageHeader';
import { toast } from 'sonner';
import { humanStatus } from '@/lib/humanise';
import { encryptId } from '@/lib/encryptId';
import { useHasRole } from '@/components/RoleGate';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';
import { studyLabel } from '@/lib/studyLabel';
import { studyNameById } from '@/lib/studyLabel';
import { subjectDisplayId } from '@/lib/subjectLabel';


const VISIT_ACTION_ROLES = [
  'platform_admin', 'application_admin', 'site_admin',
  'principal_investigator', 'sub_investigator', 'coordinator',
] as const;

/**
 * DREAM-21 — Visit Calendar (Site + Study scope).
 *
 * Single component that handles both scopes. The route /visits (site
 * calendar) reads scope='site'; /studies/:id/visits reads scope='study'
 * with the route param study_id. Month-view only for the 3-week cut
 * (day/week defer to a later sprint per the design).
 *
 * AC8 / AC12: scheduled vs unscheduled are visually distinct (striped
 * border + "Unscheduled" pill) and labelled across timeline + list.
 */

type ViewMode = 'day' | 'week' | 'month';
type ViewType = 'calendar' | 'list';
const PAGE_SIZES = [25, 50, 100] as const;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;
const CUR_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 16 }, (_, i) => CUR_YEAR - 5 + i);

// Shared filter vocabularies (calendar filter bar + list view).
const TYPE_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'unscheduled', label: 'Unscheduled' },
];
const STATUS_OPTIONS = [
  { value: 'planned', label: 'Planned' },
  { value: 'completed', label: 'Completed' },
  { value: 'missed', label: 'Missed' },
  { value: 'cancelled', label: 'Cancelled' },
];
const COMPLIANCE_OPTIONS = [
  { value: 'in_window', label: 'In window' },
  { value: 'early', label: 'Early' },
  { value: 'late', label: 'Late' },
  { value: 'excluded', label: 'Excluded' },
];

function matchesVisitFilters(
  v: VisitRow,
  f: { type: string[]; status: string[]; compliance: string[] },
): boolean {
  if (f.type.length > 0) {
    const isU = v.visit_type === 'unscheduled' || v.unscheduled === true;
    if (!f.type.includes(isU ? 'unscheduled' : 'scheduled')) return false;
  }
  if (f.status.length > 0 && !f.status.includes(v.status ?? 'planned')) return false;
  if (f.compliance.length > 0 && !f.compliance.includes(v.window_compliance ?? '')) return false;
  return true;
}

export function VisitCalendarPage() {
  const { id: studyIdRoute } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const subjectIdParam = searchParams.get('subject_id') ?? undefined;
  const scope: 'site' | 'study' | 'subject' = subjectIdParam
    ? 'subject' : studyIdRoute ? 'study' : 'site';
  const studyIdParam = studyIdRoute;

  const canAct = useHasRole(VISIT_ACTION_ROLES);

  const [viewType, setViewType] = useState<ViewType>('calendar');
  const [view, setView] = useState<ViewMode>('month');
  const [cursor, setCursor] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  });
  const [openModal, setOpenModal] = useState(false);

  // Calendar filters. Study/subject narrow the query (scope); type, status
  // and compliance narrow the fetched range client-side. A route-bound
  // study or subject (/studies/:id/visits, ?subject_id=) is fixed and not
  // shown as a filter.
  const [calStudy, setCalStudy] = useState('');
  const [calSubject, setCalSubject] = useState('');
  const [calType, setCalType] = useState<string[]>([]);
  const [calStatus, setCalStatus] = useState<string[]>([]);
  const [calCompliance, setCalCompliance] = useState<string[]>([]);
  const effectiveStudyId = studyIdParam ?? (calStudy || undefined);
  const effectiveSubjectId = subjectIdParam ?? (calSubject || undefined);
  const effectiveScope: 'site' | 'study' | 'subject' = effectiveSubjectId
    ? 'subject' : effectiveStudyId ? 'study' : 'site';
  const calFiltersActive =
    !!calStudy || !!calSubject || calType.length > 0 || calStatus.length > 0 || calCompliance.length > 0;
  const clearCalFilters = () => {
    setCalStudy(''); setCalSubject(''); setCalType([]); setCalStatus([]); setCalCompliance([]);
  };

  const calStudiesQ = useQuery({
    queryKey: ['studies-list'],
    queryFn: () => studies.list(),
    staleTime: 60_000,
    enabled: viewType === 'calendar' && scope === 'site',
  });
  const calSubjectsQ = useQuery({
    queryKey: ['subjects-list', effectiveStudyId ?? ''],
    queryFn: () => subjects.list({ study_id: effectiveStudyId, limit: 2000 }),
    staleTime: 30_000,
    enabled: viewType === 'calendar' && scope !== 'subject' && !!effectiveStudyId,
  });

  // Range derivation per view.
  const { rangeStart, queryStart, queryEnd } = useMemo(() => {
    const start = new Date(cursor);
    const end = new Date(cursor);
    if (view === 'day') {
      end.setDate(end.getDate() + 1);
      return { rangeStart: start, queryStart: start, queryEnd: end };
    } else if (view === 'week') {
      const dow = start.getDay();
      start.setDate(start.getDate() - dow);
      end.setTime(start.getTime());
      end.setDate(end.getDate() + 7);
      return { rangeStart: start, queryStart: start, queryEnd: end };
    } else {
      // rangeStart = first day of month (used by MonthGrid for month index)
      start.setDate(1);
      // MonthGrid renders 42 days starting from the Sunday on or before month start.
      // Extend the query to cover those leading/trailing overflow dates so visits
      // on days like Jul 1 or Jul 8 are fetched when viewing June.
      const gridStart = new Date(start);
      gridStart.setDate(gridStart.getDate() - gridStart.getDay()); // back to Sunday
      const gridEnd = new Date(gridStart);
      gridEnd.setDate(gridEnd.getDate() + 42);
      return { rangeStart: start, queryStart: gridStart, queryEnd: gridEnd };
    }
  }, [cursor, view]);

  const monthStart = rangeStart;

  // Effective study/subject for the calendar fetch: route-scoped values win
  // when this page is embedded under a study/subject; otherwise the new
  // filter row's picks apply. Doesn't touch the top-level `scope` const,
  // which List View's branch below still depends on.
  // const calStudyId = scope === 'study' ? studyIdParam : (filterStudy || undefined);
  // const calSubjectId = scope === 'subject' ? subjectIdParam : (filterSubject || undefined);
  // const calScope: 'site' | 'study' | 'subject' = calSubjectId ? 'subject' : calStudyId ? 'study' : 'site';

  // const studiesQ = useQuery({
  //   queryKey: ['studies-list'],
  //   queryFn: () => studies.list(),
  //   staleTime: 60_000,
  //   enabled: viewType === 'calendar',
  // });
  // const subjectsFilterStudyId = scope === 'study' ? studyIdParam : filterStudy;
  // const subjectsQ = useQuery({
  //   queryKey: ['subjects-list', subjectsFilterStudyId],
  //   queryFn: () => subjects.list({ study_id: subjectsFilterStudyId || undefined, limit: 2000 }),
  //   staleTime: 30_000,
  //   enabled: viewType === 'calendar' && scope !== 'subject',
  // });

  const visitsQ = useQuery({
    queryKey: ['visits-cal', effectiveScope, effectiveStudyId, effectiveSubjectId, queryStart.toISOString()],
    queryFn: () => visits.list({
      scope: effectiveScope,
      study_id: effectiveStudyId,
      subject_id: effectiveSubjectId,
      from: queryStart.toISOString(),
      to: queryEnd.toISOString(),
    }),
  });

  const fetchedCount = visitsQ.data?.visits.length ?? 0;
  const filteredVisits = useMemo(
    () =>
      (visitsQ.data?.visits ?? []).filter((v) =>
        matchesVisitFilters(v, { type: calType, status: calStatus, compliance: calCompliance }),
      ),
    [visitsQ.data, calType, calStatus, calCompliance],
  );

  const grouped = useMemo(() => {
    const byDay = new Map<string, VisitRow[]>();
    filteredVisits.forEach((v) => {
      // The day it took place once recorded, else the appointment.
      const date = visitCalendarWhen(v);
      if (!date) return;
      // The day on the SITE's clock (23 Oct 2026). Slicing the string read the
      // UTC day, so an evening visit in Los Angeles landed on the next day.
      const day = siteDayKey(date);
      const arr = byDay.get(day) ?? [];
      arr.push(v);
      byDay.set(day, arr);
    });
    return byDay;
  }, [filteredVisits]);

  return (
    <div className="space-y-4">
      <PageHeader
        title={
          scope === 'site'
            ? 'Site visit calendar'
            : scope === 'study'
              ? 'Study calendar'
              : 'Subject visits'
        }
        subtitle={
          viewType === 'list'
            ? 'Filter and paginate all visits across studies and subjects.'
            : view === 'day'
              ? 'Day view — all visits for this date. Click a chip to open the visit.'
              : view === 'week'
                ? 'Week view — click a day header to drill in.'
                : 'Month view — click a date or "+N more" to drill into Day view.'
        }
      />

      {/* Main view-type toggle + Add Unscheduled Visit */}
      <div className="card flex items-center justify-between gap-2 px-3 py-2">
        <div className="inline-flex rounded-md items-center gap-1 bg-surface-muted p-1">
          <button
            type="button"
            onClick={() => setViewType('calendar')}
            className={`inline-flex rounded-md items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold transition-colors ${viewType === 'calendar' ? 'bg-navy text-white shadow-card' : 'text-ink-secondary hover:bg-white/60'
              }`}
          >
            <CalendarIcon className="h-3.5 w-3.5" /> Calendar View
          </button>
          <button
            type="button"
            onClick={() => setViewType('list')}
            className={`inline-flex rounded-md items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold transition-colors ${viewType === 'list' ? 'bg-navy text-white shadow-card' : 'text-ink-secondary hover:bg-white/60'
              }`}
          >
            <LayoutList className="h-3.5 w-3.5" /> List View
          </button>
        </div>
        {canAct && (
          <button type="button" className="btn-primary text-xs" onClick={() => setOpenModal(true)}>
            <Plus className="h-3.5 w-3.5" /> Add Unscheduled Visit
          </button>
        )}
      </div>

      {/* Calendar sub-controls: Day/Week/Month + Prev/Next — hidden in list view */}
      {viewType === 'calendar' && (
        <div className="card flex flex-wrap items-center justify-between gap-3 p-3">
          <div className="inline-flex rounded-md items-center gap-1 bg-surface-muted p-1">
            {(['day', 'week', 'month'] as ViewMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setView(mode)}
                className={`rounded-md px-3.5 py-1.5 text-xs font-bold transition-colors ${view === mode ? 'bg-navy text-white shadow-card' : 'text-ink-secondary hover:bg-white/60'
                  }`}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" className="inline-flex items-center gap-1 rounded-md border border-surface-border bg-surface-base px-2.5 py-1.5 text-xs font-semibold text-ink-secondary hover:bg-surface-muted"
              onClick={() => {
                const d = new Date(cursor);
                if (view === 'day') d.setDate(d.getDate() - 1);
                else if (view === 'week') d.setDate(d.getDate() - 7);
                else d.setMonth(d.getMonth() - 1);
                setCursor(d);
              }}>
              <ChevronLeft className="h-4 w-4" /> Prev
            </button>
            {view === 'month' ? (
              <div className="flex items-center gap-1.5">
                <select
                  className="rounded-md border border-surface-border bg-surface-base py-1.5 px-2.5 text-xs font-bold text-ink-primary tabular-nums w-[120px]"
                  value={cursor.getMonth()}
                  onChange={(e) => {
                    const d = new Date(cursor);
                    d.setMonth(Number(e.target.value));
                    d.setDate(1);
                    setCursor(d);
                  }}
                >
                  {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
                </select>
                <select
                  className="rounded-md border border-surface-border bg-surface-base py-1.5 px-2.5 text-xs font-bold text-ink-primary tabular-nums w-[80px]"
                  value={cursor.getFullYear()}
                  onChange={(e) => {
                    const d = new Date(cursor);
                    d.setFullYear(Number(e.target.value));
                    d.setDate(1);
                    setCursor(d);
                  }}
                >
                  {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            ) : (
              <div className="text-sm font-semibold text-ink-primary min-w-[180px] text-center">
                {view === 'week' && `Week of ${fmtDate(monthStart)}`}
                {view === 'day' && cursor.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' })}
              </div>
            )}
            <button type="button" className="inline-flex items-center gap-1 rounded-md border border-surface-border bg-surface-base px-2.5 py-1.5 text-xs font-semibold text-ink-secondary hover:bg-surface-muted"
              onClick={() => {
                const d = new Date(cursor);
                if (view === 'day') d.setDate(d.getDate() + 1);
                else if (view === 'week') d.setDate(d.getDate() + 7);
                else d.setMonth(d.getMonth() + 1);
                setCursor(d);
              }}>
              Next <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Calendar filters — hidden in list view (it has its own) */}
      {viewType === 'calendar' && (
        <div className="card flex flex-wrap items-end gap-3 p-3" data-testid="calendar-filters">
          {scope === 'site' && (
            <div className="min-w-[200px] flex-1">
              <label className="label text-[10px]">Study</label>
              <select
                className="input h-10 text-xs"
                value={calStudy}
                onChange={(e) => { setCalStudy(e.target.value); setCalSubject(''); }}
              >
                <option value="">All studies (site)</option>
                {(calStudiesQ.data?.rows ?? []).map((s) => (
                  <option key={s.study_id} value={s.study_id}>{studyLabel(s)}</option>
                ))}
              </select>
            </div>
          )}
          {scope !== 'subject' && (
            <div className="min-w-[180px] flex-1">
              <label className="label text-[10px]">Subject</label>
              <select
                className="input h-10 text-xs"
                value={calSubject}
                disabled={!effectiveStudyId}
                onChange={(e) => setCalSubject(e.target.value)}
              >
                <option value="">{effectiveStudyId ? 'All subjects' : 'Select a study first'}</option>
                {(calSubjectsQ.data?.rows ?? []).map((s) => (
                  <option key={s.subject_id} value={s.subject_id}>{subjectDisplayId(s)}</option>
                ))}
              </select>
            </div>
          )}
          <div className="min-w-[130px]">
            <label className="label text-[10px]">Type</label>
            <MultiSelect label="types" options={TYPE_OPTIONS} value={calType} onChange={setCalType} />
          </div>
          <div className="min-w-[150px]">
            <label className="label text-[10px]">Status</label>
            <MultiSelect label="statuses" options={STATUS_OPTIONS} value={calStatus} onChange={setCalStatus} />
          </div>
          <div className="min-w-[150px]">
            <label className="label text-[10px]">Compliance</label>
            <MultiSelect label="compliance" options={COMPLIANCE_OPTIONS} value={calCompliance} onChange={setCalCompliance} />
          </div>
          <div className="ml-auto flex items-center gap-3 pb-2 text-xs text-ink-muted">
            <span>
              {visitsQ.isLoading
                ? 'Loading…'
                : calFiltersActive && filteredVisits.length !== fetchedCount
                  ? `${filteredVisits.length} of ${fetchedCount} visits in range`
                  : `${fetchedCount} visit${fetchedCount === 1 ? '' : 's'} in range`}
            </span>
            {calFiltersActive && (
              <button type="button" className="text-accent-700 hover:text-accent-800" onClick={clearCalFilters}>
                Clear filters
              </button>
            )}
          </div>
        </div>
      )}

      {/* Calendar / List body */}
      {viewType === 'list' ? (
        scope === 'subject' ? (
          <SubjectVisitList visits={visitsQ.data?.visits ?? []} onAdd={() => canAct ? setOpenModal(true) : undefined} canAct={canAct} />
        ) : (
          <ListView defaultStudyId={studyIdParam} defaultSubjectId={subjectIdParam} />
        )
      ) : visitsQ.isLoading ? (
        <div className="card flex items-center justify-center gap-2 p-10 text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading visits…
        </div>
      ) : visitsQ.isError ? (
        <div className={`card flex items-start gap-2 p-4 text-sm ${errorToneClass(visitsQ.error)}`}>
          {isPermissionError(visitsQ.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
          <span>{errorText(visitsQ.error, 'Failed to load visits.')}</span>
        </div>
      ) : view === 'day' ? (
        <DayGrid day={cursor} grouped={grouped} />
      ) : view === 'week' ? (
        <WeekGrid weekStart={monthStart} grouped={grouped}
          onDrillDay={(d) => { setCursor(d); setView('day'); }} />
      ) : (
        <MonthGrid monthStart={monthStart} grouped={grouped}
          onDrillDay={(d) => { setCursor(d); setView('day'); }} />
      )}

      {viewType === 'calendar' && !visitsQ.isLoading && !visitsQ.isError && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 px-1 text-[10.5px] font-semibold text-ink-secondary">
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-success-500" />In window</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-warning-500" />Early / attention</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-danger-500" />Late</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm border border-dashed border-warning-500" />Unscheduled</span>
        </div>
      )}

      {openModal && (
        <AddUnscheduledVisitModal
          defaultStudyId={studyIdParam}
          onClose={() => setOpenModal(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Month grid
// ---------------------------------------------------------------------------

function localDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function MonthGrid({ monthStart, grouped, onDrillDay }: { monthStart: Date; grouped: Map<string, VisitRow[]>; onDrillDay: (d: Date) => void }) {
  // Compute the 7×N grid for the month.
  const firstDayOfMonth = new Date(monthStart);
  const leadingOffset = firstDayOfMonth.getDay(); // 0=Sun
  const gridStart = new Date(monthStart);
  gridStart.setDate(gridStart.getDate() - leadingOffset);
  const days: Date[] = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    days.push(d);
  }
  const monthIndex = monthStart.getMonth();
  return (
    <div className="card overflow-hidden">
      <div className="grid grid-cols-7 border-b border-surface-border bg-surface-muted text-[10px] font-bold uppercase tracking-wider text-ink-muted">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="px-2 py-1.5">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const key = localDateKey(d);
          const events = grouped.get(key) ?? [];
          const inMonth = d.getMonth() === monthIndex;
          const today = siteTodayKey() === key;
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          return (
            <div key={key}
              className={`min-h-[88px] border-b border-r border-surface-border p-1.5 ${!inMonth ? 'bg-surface-muted text-ink-muted' : isWeekend ? 'bg-soft' : ''}`}>
              <button
                type="button"
                onClick={() => onDrillDay(d)}
                className={`mb-1 inline-flex h-[20px] w-[20px] items-center justify-center rounded text-[10.5px] font-bold tabular-nums transition-colors ${today ? 'bg-navy text-white' : inMonth ? 'text-ink-primary hover:bg-navy-50 hover:text-navy' : 'text-slate-400 hover:bg-navy-50'
                  }`}
              >
                {d.getDate()}
              </button>
              <ul className={`space-y-0.5 ${inMonth ? '' : 'opacity-40'}`}>
                {events.slice(0, 3).map((v) => (
                  <li key={v.visit_id}>
                    <VisitChip v={v} />
                  </li>
                ))}
                {events.length > 3 && (
                  <li>
                    <button
                      type="button"
                      onClick={() => onDrillDay(d)}
                      className="text-[10px] font-bold text-blue hover:underline"
                    >
                      +{events.length - 3} more
                    </button>
                  </li>
                )}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DayGrid({ day, grouped }: { day: Date; grouped: Map<string, VisitRow[]> }) {
  const key = localDateKey(day);
  const events = grouped.get(key) ?? [];
  return (
    <div className="card overflow-hidden">
      <div className="border-b border-surface-border bg-surface-muted px-3 py-2 text-xs font-bold uppercase tracking-wider text-ink-muted">
        {day.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
        <span className="ml-2 text-[10px]">· {events.length} visit{events.length === 1 ? '' : 's'}</span>
      </div>
      {events.length === 0 ? (
        <div className="p-8 text-center text-sm text-ink-muted">No visits scheduled.</div>
      ) : (
        <ul className="divide-y divide-surface-border">
          {events.map((v) => (
            <li key={v.visit_id} className="p-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-ink-primary">{v.visit_name ?? v.visit_label ?? 'Visit'}</div>
                  <div className="text-[11px] text-ink-muted">
                    Subject {v.subject_id.slice(0, 8)} ·{' '}
                    {visitCalendarWhen(v) ? fmtVisitTime(visitCalendarWhen(v)!) : ''}
                  </div>
                </div>
                <VisitChip v={v} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function WeekGrid({ weekStart, grouped, onDrillDay }: { weekStart: Date; grouped: Map<string, VisitRow[]>; onDrillDay: (d: Date) => void }) {
  const days: Date[] = [];
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    days.push(d);
  }
  const today = siteTodayKey();
  return (
    <div className="card overflow-hidden">
      <div className="grid grid-cols-7 border-b border-surface-border bg-surface-muted">
        {days.map((d) => {
          const isToday = localDateKey(d) === today;
          return (
            <button
              key={localDateKey(d)}
              type="button"
              onClick={() => onDrillDay(d)}
              className="flex items-center gap-1.5 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-ink-muted text-left hover:bg-navy-50 hover:text-navy transition-colors"
            >
              {d.toLocaleDateString(undefined, { weekday: 'short' })}
              <span className={`tabular-nums ${isToday ? 'inline-flex h-[18px] w-[18px] items-center justify-center rounded bg-navy text-white normal-case' : ''}`}>
                {d.getDate()}
              </span>
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const key = localDateKey(d);
          const events = grouped.get(key) ?? [];
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          return (
            <div key={key} className={`min-h-[160px] border-r border-surface-border p-2 ${isWeekend ? 'bg-soft' : ''}`}>
              <ul className="space-y-1">
                {events.slice(0, 5).map((v) => (
                  <li key={v.visit_id}>
                    <VisitChip v={v} />
                  </li>
                ))}
                {events.length > 5 && (
                  <li>
                    <button type="button" onClick={() => onDrillDay(d)}
                      className="text-[10px] font-bold text-blue hover:underline">
                      +{events.length - 5} more
                    </button>
                  </li>
                )}
                {events.length === 0 && (
                  <li className="text-[10px] text-ink-muted italic">No visits</li>
                )}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Status → left-border accent color for scheduled chips (Structured Ops
// styling — mirrors the same status semantics as CompliancePill/TypeBadge
// below, just as a compact border instead of a full pill).
const STATUS_BORDER_CLASS: Record<string, string> = {
  completed: 'border-l-success-500',
  missed: 'border-l-danger-500',
  cancelled: 'border-l-ink-muted',
};
const COMPLIANCE_DOT_CLASS: Record<string, string> = {
  in_window: 'bg-success-500',
  early: 'bg-warning-500',
  late: 'bg-danger-500',
  excluded: 'bg-ink-muted',
};

function VisitChip({ v }: { v: VisitRow }) {
  const isUnscheduled = v.visit_type === 'unscheduled' || v.unscheduled === true;
  const complianceDot = v.window_compliance ? COMPLIANCE_DOT_CLASS[v.window_compliance] : undefined;
  return (
    <Link
      to={`/visits/${encryptId(v.visit_id)}`}
      className={`flex items-center gap-1.5 truncate rounded px-1.5 py-0.5 text-[10px] font-medium hover:opacity-80 ${isUnscheduled
        ? 'border border-dashed border-warning-300 bg-warning-50 text-warning-800'
        : `border-l-2 bg-accent-50 text-accent-800 ${STATUS_BORDER_CLASS[v.status ?? ''] ?? 'border-l-blue'}`
        }`}
    >
      <span className="truncate flex-1">{v.visit_name ?? v.visit_label ?? 'Visit'}</span>
      {complianceDot && !isUnscheduled && <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${complianceDot}`} />}
      {isUnscheduled && (
        <span className="ml-auto shrink-0 rounded bg-warning-100 px-1 text-[9px] font-bold uppercase">U</span>
      )}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Subject visit list (AC3)
// ---------------------------------------------------------------------------

function SubjectVisitList({ visits, onAdd, canAct }: { visits: VisitRow[]; onAdd: () => void; canAct: boolean }) {
  if (visits.length === 0) {
    return (
      <div className="card p-8 text-center text-sm text-ink-muted">
        <CalendarIcon className="mx-auto mb-2 h-8 w-8 opacity-50" />
        No visits scheduled for this subject yet.
        {canAct && (
          <div className="mt-3">
            <button type="button" className="btn-secondary text-xs" onClick={onAdd}>
              <Plus className="h-3.5 w-3.5" /> Add Unscheduled Visit
            </button>
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="card overflow-hidden">
      <table className="min-w-full text-sm">
        <thead className="border-b border-surface-border bg-surface-muted text-left text-xs uppercase tracking-wide text-ink-muted">
          <tr>
            <th className="px-3 py-2">Visit</th>
            <th className="px-3 py-2">Planned</th>
            <th className="px-3 py-2">Actual date</th>
            <th className="px-3 py-2">Window</th>
            <th className="px-3 py-2">Type</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2 text-right">Open</th>
          </tr>
        </thead>
        <tbody>
          {visits.map((v) => {
            const isUnscheduled = v.visit_type === 'unscheduled' || v.unscheduled === true;
            // "Planned" is the appointment; the actual day gets its own column
            // (23 Oct 2026 — it used to replace the planned date here).
            const planned = visitWhen(v);
            const actual = visitActualDateLabel(v);
            return (
              <tr key={v.visit_id} className="border-b border-surface-border last:border-0 align-top">
                <td className="px-3 py-2 font-medium">{v.visit_name ?? v.visit_label ?? 'Visit'}</td>
                <td className="px-3 py-2 text-xs">{planned ? fmtVisitDay(planned) : '—'}</td>
                <td className={`px-3 py-2 text-xs ${actual.recorded ? '' : 'text-ink-muted'}`}>{actual.text}</td>
                <td className="px-3 py-2 text-xs text-ink-muted">
                  {v.nominal_day != null
                    ? `Day ${v.nominal_day} ${v.window_minus_days != null || v.window_plus_days != null
                      ? `± ${Math.max(v.window_minus_days ?? 0, v.window_plus_days ?? 0)}`
                      : ''}`
                    : '—'}
                </td>
                <td className="px-3 py-2">
                  <TypeBadge unscheduled={isUnscheduled} />
                </td>
                <td className="px-3 py-2 text-xs">
                  <div className="flex items-center gap-2">
                    <span>{v.status ? humanStatus(v.status) : '—'}</span>
                    {canAct && <VisitStatusMenu visit={v} />}
                  </div>
                </td>
                <td className="px-3 py-2 text-right text-xs">
                  <Link to={`/visits/${encryptId(v.visit_id)}`} className="text-accent-700 hover:underline">Open</Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CTMS-004 — Visit status menu (Completed / Missed / Cancelled)
// ---------------------------------------------------------------------------

function VisitStatusMenu({ visit }: { visit: VisitRow }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reasonFor, setReasonFor] = useState<null | 'missed' | 'cancelled'>(null);
  const [reason, setReason] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const mutate = useMutation({
    mutationFn: (input: { status: 'completed' | 'missed' | 'cancelled'; reason?: string }) =>
      visits.setStatus(visit.visit_id, input),
    onSuccess: (_d, variables) => {
      toast.success(`Visit marked ${variables.status}`);
      qc.invalidateQueries({ queryKey: ['visits-cal'] });
      qc.invalidateQueries({ queryKey: ['visits-list'] });
      qc.invalidateQueries({ queryKey: ['workspace'] });
      setOpen(false);
      setReasonFor(null);
      setReason('');
    },
    onError: (err) => toast.error(errorText(err, 'Failed to update visit status')),
  });

  // AC: only planned visits expose the menu; terminal visits are immutable.
  if (visit.status !== 'planned') return null;

  const handleOpen = () => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left });
    }
    setOpen((v) => !v);
  };

  const close = () => { setOpen(false); setReasonFor(null); setReason(''); };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="rounded border border-surface-border px-1.5 py-0.5 text-[10px] text-ink-secondary hover:bg-surface-muted"
        onClick={handleOpen}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        Set status
      </button>
      {open && (
        <>
          {/* Backdrop — only dismisses when the simple menu is open; locked while reason form is active */}
          <div
            className="fixed inset-0 z-40"
            onClick={reasonFor === null ? close : undefined}
          />
          {/* Floating panel — fixed so it never disturbs table layout */}
          <div
            className="fixed z-50 rounded-md border border-surface-border bg-white shadow-lg"
            style={{ top: pos.top, left: pos.left }}
          >
            {reasonFor === null ? (
              <ul role="menu" className="w-32 py-1">
                <li>
                  <button type="button"
                    className="block w-full px-3 py-1.5 text-left text-xs hover:bg-surface-muted"
                    onClick={() => mutate.mutate({ status: 'completed' })}
                    disabled={mutate.isPending}>
                    Completed
                  </button>
                </li>
                <li>
                  <button type="button"
                    className="block w-full px-3 py-1.5 text-left text-xs hover:bg-surface-muted"
                    onClick={() => setReasonFor('missed')}
                    disabled={mutate.isPending}>
                    Missed…
                  </button>
                </li>
                <li>
                  <button type="button"
                    className="block w-full px-3 py-1.5 text-left text-xs hover:bg-surface-muted"
                    onClick={() => setReasonFor('cancelled')}
                    disabled={mutate.isPending}>
                    Cancelled…
                  </button>
                </li>
              </ul>
            ) : (
              <div className="w-64 p-3">
                <label className="block text-[10px] font-semibold text-ink-secondary">
                  Reason for {reasonFor}
                </label>
                <textarea
                  className="input mt-1 min-h-[60px] w-full text-xs"
                  value={reason}
                  maxLength={2000}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Required — at least 3 characters"
                  autoFocus
                />
                <div className="mt-2 flex justify-end gap-1">
                  <button type="button" className="btn-ghost text-[10px]"
                    onClick={() => { setReasonFor(null); setReason(''); }}>
                    Cancel
                  </button>
                  <button type="button" className="btn-primary text-[10px]"
                    disabled={mutate.isPending || reason.trim().length < 3}
                    onClick={() => mutate.mutate({ status: reasonFor, reason: reason.trim() })}>
                    {mutate.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Confirm'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Multi-select dropdown filter
// ---------------------------------------------------------------------------

function MultiSelect({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const displayLabel =
    value.length === 0
      ? `All ${label}`
      : value.length === 1
        ? (options.find((o) => o.value === value[0])?.label ?? value[0])
        : `${value.length} selected`;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="input flex h-10 w-full items-center justify-between gap-2 text-xs"
      >
        <span className="truncate text-left">{displayLabel}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-[calc(100%+4px)] z-20 min-w-[160px] rounded-md border border-surface-border bg-white py-1 shadow-lg">
            {options.map((o) => (
              <label
                key={o.value}
                className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-surface-muted"
              >
                <input
                  type="checkbox"
                  className="rounded"
                  checked={value.includes(o.value)}
                  onChange={(e) => {
                    if (e.target.checked) onChange([...value, o.value]);
                    else onChange(value.filter((v) => v !== o.value));
                  }}
                />
                <span className="text-xs text-ink-primary">{o.label}</span>
              </label>
            ))}
            {value.length > 0 && (
              <div className="mt-1 border-t border-surface-border px-3 py-1.5">
                <button
                  type="button"
                  className="text-[10px] text-accent-700 hover:text-accent-800"
                  onClick={() => { onChange([]); setOpen(false); }}
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pill-styled multi-select — same checkbox-dropdown mechanics as MultiSelect
// above, restyled for the Calendar view's filter row only. Kept as a
// separate component so List View's existing MultiSelect (and its look)
// stays completely untouched.
// ---------------------------------------------------------------------------

// function PillMultiSelect({
//   label,
//   options,
//   value,
//   onChange,
// }: {
//   label: string;
//   options: { value: string; label: string }[];
//   value: string[];
//   onChange: (v: string[]) => void;
// }) {
//   const [open, setOpen] = useState(false);
//   const active = value.length > 0;
//   const displayLabel =
//     value.length === 0
//       ? label
//       : value.length === 1
//         ? `${label}: ${options.find((o) => o.value === value[0])?.label ?? value[0]}`
//         : `${label}: ${value.length} selected`;

//   return (
//     <div className="relative">
//       <button
//         type="button"
//         onClick={() => setOpen((v) => !v)}
//         className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-3 py-1.5 text-xs font-semibold ${active ? 'border-navy bg-navy-50 text-navy' : 'border-surface-border bg-surface-base text-ink-secondary'
//           }`}
//       >
//         <span className="truncate text-left">{displayLabel}</span>
//         <ChevronDown className={`h-3 w-3 shrink-0 ${active ? 'text-navy' : 'text-ink-muted'}`} />
//       </button>
//       {open && (
//         <>
//           <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
//           <div className="absolute left-0 top-[calc(100%+4px)] z-20 min-w-[170px] rounded-md border border-surface-border bg-white py-1 shadow-card-2">
//             {options.map((o) => (
//               <label
//                 key={o.value}
//                 className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-surface-muted"
//               >
//                 <input
//                   type="checkbox"
//                   className="rounded"
//                   checked={value.includes(o.value)}
//                   onChange={(e) => {
//                     if (e.target.checked) onChange([...value, o.value]);
//                     else onChange(value.filter((v) => v !== o.value));
//                   }}
//                 />
//                 <span className="text-xs text-ink-primary">{o.label}</span>
//               </label>
//             ))}
//             {value.length > 0 && (
//               <div className="mt-1 border-t border-surface-border px-3 py-1.5">
//                 <button
//                   type="button"
//                   className="text-[10px] font-bold text-navy hover:underline"
//                   onClick={() => { onChange([]); setOpen(false); }}
//                 >
//                   Clear
//                 </button>
//               </div>
//             )}
//           </div>
//         </>
//       )}
//     </div>
//   );
// }

// ---------------------------------------------------------------------------
// List view (AC1 / AC2 / AC3 — flat table with study + subject filters)
// ---------------------------------------------------------------------------

function ListView({ defaultStudyId, defaultSubjectId }: { defaultStudyId?: string; defaultSubjectId?: string }) {
  const canAct = useHasRole(VISIT_ACTION_ROLES);
  const [filterStudy, setFilterStudy] = useState(defaultStudyId ?? '');
  const [filterSubject, setFilterSubject] = useState(defaultSubjectId ?? '');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterType, setFilterType] = useState<string[]>([]);
  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  const [filterCompliance, setFilterCompliance] = useState<string[]>([]);

  const studiesQ = useQuery({
    queryKey: ['studies-list'],
    queryFn: () => studies.list(),
    staleTime: 60_000,
  });

  const subjectsQ = useQuery({
    queryKey: ['subjects-list', filterStudy],
    queryFn: () => subjects.list({ study_id: filterStudy || undefined, limit: 2000 }),
    staleTime: 30_000,
  });

  const scope: 'site' | 'study' | 'subject' = filterSubject ? 'subject' : filterStudy ? 'study' : 'site';
  const listQ = useQuery({
    queryKey: ['visits-list', scope, filterStudy, filterSubject],
    queryFn: () =>
      visits.list({
        scope,
        study_id: filterStudy || undefined,
        subject_id: filterSubject || undefined,
      }),
    staleTime: 20_000,
  });

  // Reset to page 1 whenever filters or page size change.
  const handleStudyChange = (val: string) => { setFilterStudy(val); setFilterSubject(''); setPage(1); };
  const handleSubjectChange = (val: string) => { setFilterSubject(val); setPage(1); };
  const handlePageSizeChange = (val: number) => { setPageSize(val); setPage(1); };
  const handleDateFromChange = (val: string) => { setFilterDateFrom(val); setPage(1); };
  const handleDateToChange = (val: string) => { setFilterDateTo(val); setPage(1); };
  const handleTypeChange = (val: string[]) => { setFilterType(val); setPage(1); };
  const handleStatusChange = (val: string[]) => { setFilterStatus(val); setPage(1); };
  const handleComplianceChange = (val: string[]) => { setFilterCompliance(val); setPage(1); };

  const studyMap = new Map<string, StudyRow>((studiesQ.data?.rows ?? []).map((s) => [s.study_id, s]));
  const subjectMap = new Map<string, SubjectRow>((subjectsQ.data?.rows ?? []).map((s) => [s.subject_id, s]));

  const allRows = useMemo(() => {
    let rows = listQ.data?.visits ?? [];
    // Compared by the visit's day at the site, like the calendar grid.
    if (filterDateFrom) {
      rows = rows.filter((v) => { const w = visitCalendarWhen(v); return !!w && siteDayKey(w) >= filterDateFrom; });
    }
    if (filterDateTo) {
      rows = rows.filter((v) => { const w = visitCalendarWhen(v); return !!w && siteDayKey(w) <= filterDateTo; });
    }
    if (filterType.length > 0) {
      rows = rows.filter((v) => {
        const isU = v.visit_type === 'unscheduled' || v.unscheduled === true;
        return filterType.includes(isU ? 'unscheduled' : 'scheduled');
      });
    }
    if (filterStatus.length > 0) {
      rows = rows.filter((v) => filterStatus.includes(v.status ?? 'planned'));
    }
    if (filterCompliance.length > 0) {
      rows = rows.filter((v) => filterCompliance.includes(v.window_compliance ?? ''));
    }
    return rows;
  }, [listQ.data, filterDateFrom, filterDateTo, filterType, filterStatus, filterCompliance]);
  const total = allRows.length;
  const totalPages = total > 0 ? Math.ceil(total / pageSize) : 1;
  const pagedRows = allRows.slice((page - 1) * pageSize, page * pageSize);
  const from = total > 0 ? (page - 1) * pageSize + 1 : 0;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="space-y-3">
      {/* Filters */}
      <div className="card flex flex-wrap items-end gap-3 p-3">
        <div className="flex-1 min-w-[200px]">
          <label className="label text-[10px]">Study</label>
          <select className="input text-xs" value={filterStudy} onChange={(e) => handleStudyChange(e.target.value)}>
            <option value="">All studies (site)</option>
            {(studiesQ.data?.rows ?? []).map((s) => (
              <option key={s.study_id} value={s.study_id}>{studyLabel(s)}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="label text-[10px]">Subject</label>
          <select className="input text-xs" value={filterSubject}
            onChange={(e) => handleSubjectChange(e.target.value)} disabled={!filterStudy}>
            <option value="">{filterStudy ? 'All subjects' : 'Select a study first'}</option>
            {(subjectsQ.data?.rows ?? []).map((s) => (
              <option key={s.subject_id} value={s.subject_id}>{subjectDisplayId(s)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Row 2: date range + type / status / compliance */}
      <div className="card flex flex-wrap items-end gap-3 p-3">
        <div className="min-w-[140px]">
          <label className="label text-[10px]">Date from</label>
          <input type="date" className="input h-10 text-xs" value={filterDateFrom}
            onChange={(e) => handleDateFromChange(e.target.value)} />
        </div>
        <div className="min-w-[140px]">
          <label className="label text-[10px]">Date to</label>
          <input type="date" className="input h-10 text-xs" value={filterDateTo}
            onChange={(e) => handleDateToChange(e.target.value)} />
        </div>
        <div className="min-w-[130px]">
          <label className="label text-[10px]">Type</label>
          <MultiSelect
            label="types"
            options={[
              { value: 'scheduled', label: 'Scheduled' },
              { value: 'unscheduled', label: 'Unscheduled' },
            ]}
            value={filterType}
            onChange={handleTypeChange}
          />
        </div>
        <div className="min-w-[150px]">
          <label className="label text-[10px]">Status</label>
          <MultiSelect
            label="statuses"
            options={[
              { value: 'planned', label: 'Planned' },
              { value: 'completed', label: 'Completed' },
              { value: 'missed', label: 'Missed' },
              { value: 'cancelled', label: 'Cancelled' },
            ]}
            value={filterStatus}
            onChange={handleStatusChange}
          />
        </div>
        <div className="min-w-[150px]">
          <label className="label text-[10px]">Compliance</label>
          <MultiSelect
            label="compliance"
            options={[
              { value: 'in_window', label: 'In window' },
              { value: 'early', label: 'Early' },
              { value: 'late', label: 'Late' },
              { value: 'excluded', label: 'Excluded' },
            ]}
            value={filterCompliance}
            onChange={handleComplianceChange}
          />
        </div>
      </div>

      {/* Table */}
      {listQ.isLoading || subjectsQ.isLoading ? (
        <div className="card flex items-center justify-center gap-2 p-10 text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading visits…
        </div>
      ) : listQ.isError ? (
        <div className={`card flex items-start gap-2 p-4 text-sm ${errorToneClass(listQ.error)}`}>
          {isPermissionError(listQ.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
          {errorText(listQ.error, 'Failed to load visits.')}
        </div>
      ) : total === 0 ? (
        <div className="card p-10 text-center text-sm text-ink-muted">No visits found for this selection.</div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-surface-border bg-surface-muted text-left text-xs uppercase tracking-wide text-ink-muted">
                <tr>
                  <th className="px-3 py-2">Visit</th>
                  <th className="px-3 py-2">Subject</th>
                  <th className="px-3 py-2">Study</th>
                  <th className="px-3 py-2">Planned date</th>
                  <th className="px-3 py-2">Actual date</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Compliance</th>
                  <th className="px-3 py-2 text-right">Open</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((v) => {
                  const isUnscheduled = v.visit_type === 'unscheduled' || v.unscheduled === true;
                  const subjectRow = subjectMap.get(v.subject_id);
                  const subjectRef = subjectRow ? subjectDisplayId(subjectRow) : v.subject_id.slice(0, 8);
                  const studyRef = studyNameById(studyMap, v.study_id);
                  const planned = visitWhen(v);
                  const actual = visitActualDateLabel(v);
                  return (
                    <tr key={v.visit_id} className="border-b border-surface-border last:border-0 hover:bg-surface-muted/30">
                      <td className="px-3 py-2 font-medium text-ink-primary">{v.visit_name ?? v.visit_label ?? 'Visit'}</td>
                      <td className="px-3 py-2 text-xs text-ink-secondary">{subjectRef}</td>
                      <td className="px-3 py-2 text-xs text-ink-secondary">{studyRef}</td>
                      <td className="px-3 py-2 text-xs">{planned ? fmtVisitDay(planned) : '—'}</td>
                      <td className={`px-3 py-2 text-xs ${actual.recorded ? '' : 'text-ink-muted'}`}>{actual.text}</td>
                      <td className="px-3 py-2">
                        <TypeBadge unscheduled={isUnscheduled} />
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div className="flex items-center gap-1.5">
                          <span>{humanStatus(v.status ?? null)}</span>
                          {canAct && <VisitStatusMenu visit={v} />}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs"><CompliancePill value={v.window_compliance ?? null} /></td>
                      <td className="px-3 py-2 text-right text-xs">
                        <Link to={`/visits/${encryptId(v.visit_id)}`} className="text-accent-700 hover:underline">Open</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          <div className="flex items-center justify-between border-t border-surface-border px-4 py-3">
            <span className="text-xs text-ink-muted">
              {from}–{to} of {total.toLocaleString()} visit{total === 1 ? '' : 's'}
            </span>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-ink-muted">Rows per page:</label>
                <select className="input py-1 text-xs w-20" value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}>
                  {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => setPage(1)} disabled={page <= 1}
                  className="btn-secondary py-1 px-2 text-xs disabled:opacity-40" title="First page">
                  <ChevronsLeft className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}
                  className="btn-secondary py-1 px-2 text-xs disabled:opacity-40" title="Previous page">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-2 text-xs font-medium text-ink-secondary">
                  Page {page} of {totalPages}
                </span>
                <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages}
                  className="btn-secondary py-1 px-2 text-xs disabled:opacity-40" title="Next page">
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => setPage(totalPages)} disabled={page >= totalPages}
                  className="btn-secondary py-1 px-2 text-xs disabled:opacity-40" title="Last page">
                  <ChevronsRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TypeBadge({ unscheduled }: { unscheduled: boolean }) {
  return unscheduled ? (
    <span className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
      Unscheduled
    </span>
  ) : (
    <span className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-sky-50 text-sky-700 border border-sky-200">
      Scheduled
    </span>
  );
}

function CompliancePill({ value }: { value: string | null }) {
  if (!value) return <>—</>;
  const meta: Record<string, { label: string; cls: string }> = {
    in_window: { label: 'In window', cls: 'bg-success-50 text-success-700' },
    early: { label: 'Early', cls: 'bg-warning-50 text-warning-700' },
    late: { label: 'Late', cls: 'bg-danger-50 text-danger-700' },
    excluded: { label: 'Excluded', cls: 'bg-surface-muted text-ink-muted' },
  };
  const m = meta[value] ?? { label: value, cls: 'bg-surface-muted text-ink-secondary' };
  return <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${m.cls}`}>{m.label}</span>;
}

// ---------------------------------------------------------------------------
// Add Unscheduled Visit modal
// ---------------------------------------------------------------------------

function AddUnscheduledVisitModal({ defaultStudyId, onClose }: { defaultStudyId?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [studyId, setStudyId] = useState(defaultStudyId ?? '');
  const [subjectId, setSubjectId] = useState('');
  // Date AND time — an unscheduled visit happens at a moment, and the
  // calendar renders that time.
  const [visitDate, setVisitDate] = useState(() => toDateTimeInput(new Date().toISOString()));
  const [reason, setReason] = useState('');

  // Dropdown data: all studies for this site.
  const studiesQ = useQuery({
    queryKey: ['studies-list'],
    queryFn: () => studies.list(),
    staleTime: 60_000,
  });

  // Subjects filtered by selected study.
  const subjectsQ = useQuery({
    queryKey: ['subjects-list', studyId],
    queryFn: () => subjects.list({ study_id: studyId, limit: 200 }),
    enabled: !!studyId,
    staleTime: 30_000,
  });

  const create = useMutation({
    mutationFn: () =>
      visits.createUnscheduled({
        study_id: studyId,
        subject_id: subjectId,
        visit_date: fromDateTimeInput(visitDate),
        reason: reason || undefined,
      }),
    onSuccess: () => {
      toast.success('Unscheduled visit added');
      qc.invalidateQueries({ queryKey: ['visits-cal'] });
      qc.invalidateQueries({ queryKey: ['workspace'] });
      onClose();
    },
    onError: (err) => toast.error(errorText(err, 'Failed to add visit')),
  });

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 !m-0">
      <form
        className="card w-full max-w-md space-y-4 p-6"
        onSubmit={(e) => { e.preventDefault(); if (studyId && subjectId) create.mutate(); }}
      >
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-semibold">Add Unscheduled Visit</h3>
          <button type="button" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        {/* Study dropdown */}
        <div>
          <label className="label text-xs">Study *</label>
          <select
            className="input"
            required
            value={studyId}
            onChange={(e) => { setStudyId(e.target.value); setSubjectId(''); }}
            disabled={studiesQ.isLoading}
          >
            <option value="">
              {studiesQ.isLoading ? 'Loading studies…' : '— Select a study —'}
            </option>
            {(studiesQ.data?.rows ?? []).map((s) => (
              <option key={s.study_id} value={s.study_id}>
                {studyLabel(s)}
              </option>
            ))}
          </select>
        </div>

        {/* Subject dropdown — enabled once a study is chosen */}
        <div>
          <label className="label text-xs">Subject *</label>
          <select
            className="input"
            required
            value={subjectId}
            onChange={(e) => setSubjectId(e.target.value)}
            disabled={!studyId || subjectsQ.isLoading}
          >
            <option value="">
              {!studyId
                ? 'Select a study first'
                : subjectsQ.isLoading
                  ? 'Loading subjects…'
                  : subjectsQ.data?.rows.length === 0
                    ? 'No subjects for this study'
                    : '— Select a subject —'}
            </option>
            {(subjectsQ.data?.rows ?? []).map((sub) => (
              <option key={sub.subject_id} value={sub.subject_id}>
                {subjectDisplayId(sub)}
              </option>
            ))}
          </select>
        </div>

        {/* Date */}
        <div>
          <label className="label text-xs">Visit date and time *</label>
          <input
            className="input"
            type="datetime-local"
            required
            value={visitDate}
            onChange={(e) => setVisitDate(e.target.value)}
          />
          <p className="mt-1 text-[10px] text-ink-muted">
            Defaults to now; change if the visit happened at another time.
          </p>
        </div>

        {/* Reason */}
        <div>
          <label className="label text-xs">Reason / note (optional)</label>
          <textarea
            className="input min-h-[60px]"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. AE follow-up, additional lab draw, …"
            maxLength={500}
          />
        </div>

        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button
            type="submit"
            className="btn-primary"
            disabled={create.isPending || !studyId || !subjectId}
          >
            {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add visit
          </button>
        </div>
      </form>
    </div>
  );
}
